/**
 * Fixed Assets — Division Settings modal
 * ----------------------------------------------------------
 * "Eligible for Additional Depreciation" toggle (Sec 32(1)(iia)). One-time
 * setting per division — locks after the first save. Once locked, only a
 * Super Admin can override it.
 */
import React, { useEffect, useState } from "react";
import { X, ShieldCheck, Lock, Check } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function DivisionSettingsModal({ runId, onClose }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    let alive = true;
    http.get(`/fixed-assets/runs/${runId}/division-settings`).then(({ data }) => {
      if (!alive) return;
      setData(data);
      setEligible(!!data?.additional_dep_eligible);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [runId]);

  const locked = !!data?.locked && !isSuperAdmin;

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await http.post(`/fixed-assets/runs/${runId}/division-settings`, {
        additional_dep_eligible: eligible,
      });
      setData(saved);
      toast.success("Depreciation settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save — you may need Super Admin to override.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="fa-division-settings-modal">
      <div className="bg-white w-full max-w-md border border-slate-300 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
            <ShieldCheck size={15} className="text-emerald-700"/> Depreciation Settings
          </div>
          <button data-testid="fa-division-settings-close" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-[12px] text-slate-500">Loading…</div>
          ) : (
            <>
              <label className="flex items-start gap-3 p-3 border border-slate-200 bg-slate-50/60 cursor-pointer">
                {/* Custom-rendered box (not a native <input type="checkbox">) —
                    a prior native-checkbox implementation rendered visually
                    empty in checked+disabled state despite correct DOM state,
                    misleading auditors into thinking the setting was OFF. This
                    fully-controlled box + the ON/OFF badge below make the
                    state unambiguous regardless of browser/CSS quirks. */}
                <button
                  type="button"
                  data-testid="fa-additional-dep-eligible-toggle"
                  role="checkbox"
                  aria-checked={eligible}
                  disabled={locked}
                  onClick={() => !locked && setEligible(!eligible)}
                  className={`mt-0.5 shrink-0 w-[18px] h-[18px] border-2 flex items-center justify-center transition-colors ${
                    eligible
                      ? "bg-emerald-600 border-emerald-600"
                      : "bg-white border-slate-400"
                  } ${locked ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:border-emerald-500"}`}
                >
                  {eligible && <Check size={13} strokeWidth={3.5} className="text-white"/>}
                </button>
                <div>
                  <div className="text-[12.5px] font-medium text-slate-800 flex items-center gap-2">
                    Eligible for Additional Depreciation (Sec 32(1)(iia))
                    <span
                      data-testid="fa-additional-dep-eligible-status-badge"
                      className={`text-[9.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                        eligible
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                          : "bg-slate-100 border-slate-300 text-slate-600"
                      }`}
                    >
                      {eligible ? "ON" : "OFF"}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Enables the 20% additional depreciation allowance for eligible new
                    Plant &amp; Machinery (15% Block) additions{data?.division_label ? ` in ${data.division_label}` : " for this entity"}. This is a
                    one-time setting — it locks after the first save.
                  </div>
                </div>
              </label>

              {data?.locked && (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2">
                  <Lock size={12}/>
                  {isSuperAdmin
                    ? "Locked — you are overriding this as Super Admin."
                    : `Locked by ${data.set_by_name || data.set_by_email || "an admin"}. Only a Super Admin can change it now.`}
                </div>
              )}

              <button
                data-testid="fa-division-settings-save"
                onClick={save}
                disabled={saving || locked}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-white text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed ${locked ? "bg-slate-400" : "bg-emerald-600 hover:bg-emerald-700"}`}
              >
                {saving ? "Saving…" : locked ? "Locked" : "Save Setting"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
